import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Send, Bookmark, ChevronLeft, ChevronRight, Image as ImageIcon, Music2, MoreHorizontal } from 'lucide-react';
import { isVideo } from './content';

/**
 * Anteprima "come apparirà su Instagram" di un contenuto del PED.
 * - Post / Carosello: riquadro quadrato con scorrimento tra le slide, azioni, caption.
 * - Reel / Storia: formato verticale 9:16 con testo sovrapposto.
 * Non è una riproduzione pixel-perfect: serve a SMM e clienti per capire
 * a colpo d'occhio impaginazione, lunghezza della caption e ordine delle slide.
 */
function Media({ url, fit = 'cover' }) {
  if (!url) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#8e8e8e', background: '#efefef' }}>
        <ImageIcon size={30} />
        <span style={{ fontSize: 12 }}>Nessun media</span>
      </div>
    );
  }
  return isVideo(url)
    ? <video src={url} muted loop playsInline autoPlay style={{ width: '100%', height: '100%', objectFit: fit, background: '#000' }} />
    : <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: fit, background: '#000' }} />;
}

function Caption({ nome, testo }) {
  const [aperta, setAperta] = useState(false);
  if (!testo) return <div style={{ color: '#8e8e8e', fontSize: 13 }}>Nessuna caption</div>;
  const lungo = testo.length > 125;
  const mostrato = !lungo || aperta ? testo : `${testo.slice(0, 125).trimEnd()}…`;
  // hashtag e menzioni in blu come nell'app
  const parti = mostrato.split(/([#@][\p{L}\p{N}_.]+)/u);
  return (
    <div style={{ fontSize: 13, lineHeight: 1.4, color: '#262626', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      <b style={{ marginRight: 5 }}>{nome}</b>
      {parti.map((p, i) => (/^[#@]/.test(p) ? <span key={i} style={{ color: '#00376b' }}>{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>))}
      {lungo && !aperta && (
        <button onClick={() => setAperta(true)} style={{ background: 'none', border: 'none', color: '#8e8e8e', padding: 0, marginLeft: 4, cursor: 'pointer', font: 'inherit', minHeight: 0 }}>altro</button>
      )}
    </div>
  );
}

export default function InstagramPreview({ formato, media = [], caption, cliente, titolo }) {
  const [i, setI] = useState(0);
  useEffect(() => { if (i >= media.length) setI(0); }, [media.length, i]);
  const handle = (cliente || 'account').toLowerCase().replace(/[^a-z0-9._]+/g, '').slice(0, 24) || 'account';
  const verticale = formato === 'Reel' || formato === 'Storia' || formato === 'Video YT';
  const iniziale = (cliente || '?').trim().charAt(0).toUpperCase();

  const Avatar = ({ size = 32 }) => (
    <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, padding: 2, background: 'linear-gradient(45deg,#f9ce34,#ee2a7b,#6228d7)', display: 'inline-flex' }}>
      <span style={{ flex: 1, borderRadius: '50%', background: '#fff', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, color: '#262626' }}>{iniziale}</span>
    </span>
  );

  const frame = {
    width: '100%', maxWidth: 340, margin: '0 auto', background: '#fff', color: '#262626',
    borderRadius: 14, overflow: 'hidden', border: '1px solid #dbdbdb',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  };

  if (verticale) {
    const storia = formato === 'Storia';
    return (
      <div style={{ ...frame, maxWidth: 280, aspectRatio: '9 / 16', position: 'relative', background: '#000' }}>
        <Media url={media[i]} />
        {storia && (
          <div style={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', gap: 3 }}>
            {(media.length ? media : [null]).map((_, k) => (
              <span key={k} style={{ flex: 1, height: 2.5, borderRadius: 2, background: k <= i ? '#fff' : 'rgba(255,255,255,0.4)' }} />
            ))}
          </div>
        )}
        <div style={{ position: 'absolute', top: storia ? 18 : 12, left: 10, right: 10, display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
          <Avatar size={28} />
          <b style={{ fontSize: 12.5, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{handle}</b>
          <MoreHorizontal size={18} style={{ marginLeft: 'auto' }} />
        </div>
        {!storia && (
          <>
            <div style={{ position: 'absolute', right: 10, bottom: 70, display: 'flex', flexDirection: 'column', gap: 16, color: '#fff' }}>
              <Heart size={24} /><MessageCircle size={24} /><Send size={24} />
            </div>
            <div style={{ position: 'absolute', left: 12, right: 50, bottom: 14, color: '#fff', fontSize: 12.5, lineHeight: 1.35, textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}>
              <div style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{caption || titolo || ''}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 11.5 }}><Music2 size={12} /> Audio originale</div>
            </div>
          </>
        )}
        {media.length > 1 && (
          <>
            <button aria-label="Precedente" onClick={() => setI(k => Math.max(0, k - 1))} style={navBtn('left')}><ChevronLeft size={16} /></button>
            <button aria-label="Successivo" onClick={() => setI(k => Math.min(media.length - 1, k + 1))} style={navBtn('right')}><ChevronRight size={16} /></button>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={frame}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px' }}>
        <Avatar />
        <b style={{ fontSize: 13 }}>{handle}</b>
        <MoreHorizontal size={18} style={{ marginLeft: 'auto', color: '#262626' }} />
      </div>
      <div style={{ position: 'relative', aspectRatio: formato === 'Carosello' ? '4 / 5' : '1 / 1', background: '#efefef' }}>
        <Media url={media[i]} />
        {media.length > 1 && (
          <>
            <span style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 999 }}>{i + 1}/{media.length}</span>
            <button aria-label="Slide precedente" onClick={() => setI(k => Math.max(0, k - 1))} style={navBtn('left')} disabled={i === 0}><ChevronLeft size={16} /></button>
            <button aria-label="Slide successiva" onClick={() => setI(k => Math.min(media.length - 1, k + 1))} style={navBtn('right')} disabled={i === media.length - 1}><ChevronRight size={16} /></button>
          </>
        )}
      </div>
      <div style={{ padding: '10px 12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
          <Heart size={22} /><MessageCircle size={22} /><Send size={22} />
          {media.length > 1 && (
            <span style={{ display: 'flex', gap: 4, margin: '0 auto' }}>
              {media.map((_, k) => <span key={k} style={{ width: 6, height: 6, borderRadius: '50%', background: k === i ? '#0095f6' : '#c7c7c7' }} />)}
            </span>
          )}
          <Bookmark size={22} style={{ marginLeft: media.length > 1 ? 0 : 'auto' }} />
        </div>
        <Caption nome={handle} testo={caption} />
      </div>
    </div>
  );
}

function navBtn(side) {
  return {
    position: 'absolute', top: '50%', [side]: 8, transform: 'translateY(-50%)',
    width: 28, height: 28, minHeight: 0, borderRadius: '50%', border: 'none', cursor: 'pointer',
    background: 'rgba(255,255,255,0.85)', color: '#262626', display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
  };
}
